import React from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { IphoneMockImage } from "../DeviceMocks";
import clsx from "clsx";

interface ServiceCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
  image?: {
    src: string;
    webp: string;
    type: string;
  };
  className?: string;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  icon: Icon,
  title,
  description,
  href,
  image,
  className,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      viewport={{ once: true }}
      className={clsx("group h-full", className)}
    >
      <Link
        to={href}
        className="relative rounded-xl flex flex-row items-stretch overflow-hidden bg-white dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-lg hover:shadow-gray-500/5 dark:hover:shadow-gray-900/20 duration-300 group h-full backdrop-blur-sm"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/30 via-slate-50/20 to-gray-100/30 dark:from-gray-800/20 dark:via-gray-700/10 dark:to-gray-800/20 opacity-0 group-hover:opacity-100 transition-all duration-300" />

        <div className="py-6 sm:py-8 lg:py-10 px-4 sm:px-6 lg:px-8 gap-4 sm:gap-5 flex flex-col justify-center w-full sm:w-[70%] relative z-10">
          <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-slate-600 to-gray-700 dark:from-slate-500 dark:to-gray-600 shadow-sm transition-transform duration-200 ease-out group-hover:scale-105">
            <Icon className="w-6 h-6 text-white" />
          </div>

          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors duration-300">
            {title}
          </h3>

          <p className="text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 leading-relaxed transition-colors duration-300 text-sm">
            {description}
          </p>

          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 font-medium text-sm transition-transform duration-200 ease-out group-hover:translate-x-1 group-hover:text-blue-700 dark:group-hover:text-blue-300">
            <span>Explore feature</span>
            <ArrowRightIcon className="w-4 h-4" />
          </div>
        </div>

        {image && (
          <div className="relative items-center justify-center sm:w-[30%] flex-none">
            <div className="w-[160px] h-[533px] sm:w-[240px] sm:h-auto lg:w-auto transition-transform duration-200 ease-out group-hover:scale-[1.02]">
              <IphoneMockImage
                alt={title}
                image={image}
                className="w-full h-full mx-auto drop-shadow-lg"
              />
            </div>
          </div>
        )}
      </Link>
    </motion.div>
  );
}; 